import { Box, Stack } from "@mui/material";
import { useLocalStorage } from "@src/appV2/lib/utils/useLocalstorage";
import { Button } from "@src/appV2/redesign/components/Button";
import { Text } from "@src/appV2/redesign/components/Text";
import { useIsRebrand } from "@src/appV2/redesign/theme/RebrandProvider";
import {
  type AccountSwitcherUser,
  type AdminSearchTabValue,
  type UserDetails,
  ADMIN_SEARCH_PLACEHOLDER,
  ADMIN_SEARCH_TAB_LABELS,
  ADMIN_SEARCH_TAB_OPTIONS,
  ADMIN_SEARCH_TAB_STORAGE_KEY,
} from "../../Shared/AccountSwitcher/types";
import { AccountSwitcherOptionGroupMobile } from "./AccountSwitcherOptionGroup";

interface AccountSwitcherDropdownAdminMobileProps {
  workerDetails: UserDetails;
  workplaceDetails: UserDetails;
  onUserClicked: (user: AccountSwitcherUser) => void;
  hasSearchTerm: boolean;
}

export function AccountSwitcherDropdownAdminMobile(props: AccountSwitcherDropdownAdminMobileProps) {
  const { workerDetails, workplaceDetails, onUserClicked, hasSearchTerm } = props;
  const isRebrand = useIsRebrand();
  const [selectedTab, setSelectedTab] = useLocalStorage<AdminSearchTabValue>(
    ADMIN_SEARCH_TAB_STORAGE_KEY,
    "all"
  );

  if (!hasSearchTerm) {
    return (
      <Box sx={{ padding: 4, textAlign: "center" }}>
        <Text
          variant="body2"
          sx={(theme) => ({
            color: isRebrand ? theme.palette.base?.muted : theme.palette.navbar?.itemContent,
          })}
        >
          {ADMIN_SEARCH_PLACEHOLDER}
        </Text>
      </Box>
    );
  }

  const showAgents = selectedTab === "agents" || selectedTab === "all";
  const showWorkplaces = selectedTab === "workplaces" || selectedTab === "all";
  const isAllMode = selectedTab === "all";
  const inactiveTabVariant = isRebrand ? "outlined" : "outlineInverted";

  return (
    <Stack>
      {/* Tabs */}
      <Stack direction="row" gap={1} justifyContent="center" sx={{ paddingY: 3 }}>
        {ADMIN_SEARCH_TAB_OPTIONS.map((tab) => (
          <Button
            key={tab}
            size="small"
            variant={selectedTab === tab ? "primary" : inactiveTabVariant}
            onClick={() => setSelectedTab(tab)}
            sx={(theme) => ({
              textTransform: "capitalize",
              flex: 1,
              ...(isRebrand &&
                selectedTab === tab && {
                  backgroundColor: theme.palette.base?.strong,
                  color: theme.palette.base?.inverted?.base,
                  "&:hover": {
                    backgroundColor: theme.palette.base?.strong,
                  },
                }),
            })}
          >
            {ADMIN_SEARCH_TAB_LABELS[tab]}
          </Button>
        ))}
      </Stack>

      {/* Content */}
      <Box>
        {showAgents && (
          <Box data-testid="account-switcher-agents">
            {isAllMode && (
              <Text
                variant="body2"
                sx={(theme) => ({
                  color: isRebrand
                    ? theme.palette.base?.strong
                    : theme.palette.navbar?.itemContentSelected,
                  paddingX: 2,
                  paddingY: 1,
                  fontWeight: 600,
                })}
              >
                AGENTS
              </Text>
            )}
            <AccountSwitcherOptionGroupMobile
              userDetails={workerDetails}
              onUserClicked={onUserClicked}
              loadMoreLabel="Load more agents"
            />
          </Box>
        )}

        {showWorkplaces && (
          <Box sx={{ marginTop: isAllMode ? 2 : 0 }}>
            {isAllMode && (
              <Text
                variant="body2"
                sx={(theme) => ({
                  color: isRebrand
                    ? theme.palette.base?.strong
                    : theme.palette.navbar?.itemContentSelected,
                  paddingX: 2,
                  paddingY: 1,
                  fontWeight: 600,
                })}
              >
                WORKPLACES
              </Text>
            )}
            <AccountSwitcherOptionGroupMobile
              userDetails={workplaceDetails}
              onUserClicked={onUserClicked}
              loadMoreLabel="Load more workplaces"
            />
          </Box>
        )}
      </Box>
    </Stack>
  );
}
